/* eslint-disable @typescript-eslint/no-explicit-any */
import { toast } from "react-toastify";
import { API_URL } from "../constants";
import { IApiError } from "../types";
import { decimalToHex } from "../ui/HexInput";

interface IMakeApiRequest {
  path: string;
  method?: string;
  body?: string;
  token?: string | null;
  org?: string;
  emptyResponse?: boolean;
  reloadOnAuthError?: boolean;
  signal?: any;
}

export const errorAlert = (e: any) => {
  try {
    toast.error(JSON.parse(e.message).message);
  } catch (err: any) {
    toast.error(e.toString());
  }
};

export const successAlert = (msg: string) => toast.success(msg);

export async function makeApiRequest<T>({
  path,
  method = "GET",
  body,
  token,
  org,
  emptyResponse = false,
  reloadOnAuthError = true,
  signal,
}: IMakeApiRequest): Promise<T> {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  myHeaders.append("Accept", "application/json");
  if (token) {
    myHeaders.append(`Authorization`, `Bearer ${token}`);
  }
  if (org) {
    myHeaders.append(`X-ORG-ID`, org);
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    body,
    headers: myHeaders,
    signal,
  });

  if (!response.ok) {
    const errorResponse: IApiError = await response.json();
    if (+response.status === 401 && reloadOnAuthError) {
      localStorage.removeItem("token");
      window.location.reload();
    }
    throw new Error(
      JSON.stringify({
        message: errorResponse.Message,
        status: response.status,
      })
    );
  }

  if (emptyResponse) {
    return response as T;
  }
  const responseData: T = await response.json();
  return responseData;
}

// this fucntion displays both hex and dec values dataTable for XSSI Connection
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformXSSIRows = (rows: any, hexMode: boolean) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "CONNECTION-ID": row["CONNECTION-ID"],
    "RADIO-IPV4-ADDR": row["RADIO-IPV4-ADDR"],
    "RADIO-RFSS-ID":
      (row["RADIO-RFSS-ID"] === 0 && hexMode) ||
      (row["RADIO-RFSS-ID"] && hexMode)
        ? `${decimalToHex(row["RADIO-RFSS-ID"])} (${row["RADIO-RFSS-ID"]})`
        : `${row["RADIO-RFSS-ID"]} (${decimalToHex(row["RADIO-RFSS-ID"])})`,
    "RADIO-SYSTEM-ID":
      (row["RADIO-SYSTEM-ID"] === 0 && hexMode) ||
      (row["RADIO-SYSTEM-ID"] && hexMode)
        ? `${decimalToHex(row["RADIO-SYSTEM-ID"])} (${row["RADIO-SYSTEM-ID"]})`
        : `${row["RADIO-SYSTEM-ID"]} (${decimalToHex(row["RADIO-SYSTEM-ID"])})`,
    "RADIO-WACN-ID":
      (row["RADIO-WACN-ID"] === 0 && hexMode) ||
      (row["RADIO-WACN-ID"] && hexMode)
        ? `${decimalToHex(row["RADIO-WACN-ID"])} (${row["RADIO-WACN-ID"]})`
        : `${row["RADIO-WACN-ID"]} (${decimalToHex(row["RADIO-WACN-ID"])})`,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLMRGroupsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "TG-ID": row["TG-ID"],
    "MAPPED-MCPTT-GID": row["MAPPED-MCPTT-GID"],
    "LMR-NW-TYPE": row["LMR-NW-TYPE"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIConnectionsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "CONNECTION-ID": row["CONNECTION-ID"],
    "DFSI-VERSION": row["DFSI-VERSION"],
    "HOST-CONTROL-IPV4-ADDR": row["HOST-CONTROL-IPV4-ADDR"],
    "HOST-VOICE-IPV4-ADDR": row["HOST-VOICE-IPV4-ADDR"],
    "FIXED-STATION-CONTROL-IPV4-ADDR": row["FIXED-STATION-CONTROL-IPV4-ADDR"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIChannelsRows = (rows: any, hexMode: boolean) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "CONNECTION-ID": row["CONNECTION-ID"],
    "CHANNEL-ID": row["CHANNEL-ID"],
    "CHANNEL-MODE": row["CHANNEL-MODE"],
    "OUTBOUND-NETWORK-ACCESS-CODE":
      row["OUTBOUND-NETWORK-ACCESS-CODE"] && hexMode
        ? decimalToHex(row["OUTBOUND-NETWORK-ACCESS-CODE"])
        : row["OUTBOUND-NETWORK-ACCESS-CODE"],
    "INBOUND-NETWORK-ACCESS-CODE":
      row["INBOUND-NETWORK-ACCESS-CODE"] && hexMode
        ? decimalToHex(row["INBOUND-NETWORK-ACCESS-CODE"])
        : row["INBOUND-NETWORK-ACCESS-CODE"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIGroupsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "DFSI-LOGICAL-GID": row["DFSI-LOGICAL-GID"],
    "DFSI-GROUP-NAME": row["DFSI-GROUP-NAME"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLMRUsersRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "LMR-ID": row["LMR-ID"],
    "MAPPED-MCPTT-ID": row["MAPPED-MCPTT-ID"],
    "LMR-NW-TYPE": row["LMR-NW-TYPE"],
    "MUTUAL-AID": row["MUTUAL-AID"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMCPTTGroupsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "MCPTT-GID": row["MCPTT-GID"],
    "MAPPED-P25-TG-ID": row["MAPPED-P25-TG-ID"],
    "MAPPED-DMR-TG-ID": row["MAPPED-DMR-TG-ID"],
    "MAPPED-ROIP-TG-ID": row["MAPPED-ROIP-TG-ID"],
    "MAPPED-DFSI-TG-ID": row["MAPPED-DFSI-TG-ID"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMCPTTUsersRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "MCPTT-ID": row["MCPTT-ID"],
    "MAPPED-P25-ID": row["MAPPED-P25-ID"],
    "MAPPED-DMR-ID": row["MAPPED-DMR-ID"],
    "MAPPED-ROIP-ID": row["MAPPED-ROIP-ID"],
    "MAPPED-DFSI-ID": row["MAPPED-DFSI-ID"],
    "DISPLAY-NAME": row["DISPLAY-NAME"],
    "MUTUAL-AID": row["MUTUAL-AID"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOTARRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "CONNECTION-ID": row["CONNECTION-ID"],
    "KMF-RADIO-SET-IDENTIFIER": row["KMF-RADIO-SET-IDENTIFIER"],
    "KMF-PORT": row["KMF-PORT"],
    "KMF-IPV4-ADDR": row["KMF-IPV4-ADDR"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformCannedMessagesRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "MSG-ID": row["MSG-ID"],
    "CANNED-TEXT": row["CANNED-TEXT"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformCannedMessageUpdateBody = (data: any) => {
  return JSON.stringify({
    "CANNED-TEXT": data["CANNED-TEXT"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOrganizationRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row["ORG-ID"],
    "ORG-ID": row["ORG-ID"],
    "ORG-NAME": row["ORG-NAME"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOrganizationTranslationRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((r: any) => ({
    id: `${r.ID}`,
    ID: r.ID,
    EFI: `${r.EFI}`,
    EFO: `${r.EFO}`,
    FROM: r.FROM,
    TO: r.TO,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLocationsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "IWF-ID": row["IWF-ID"],
    "IWF-LOCATION-NAME": row["IWF-LOCATION-NAME"],
    "IWF-DOMAIN-NAME": row["IWF-DOMAIN-NAME"],
    CreationDate: row.CreationDate,
    ModificationDate: row.ModificationDate,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformRealtimeRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: `${row["GROUP-ID"]} - ${row["USER-ID"]} - ${row["SECS-TO-EXPIRE"]}`,
    "GROUP-ID": row["GROUP-ID"],
    "USER-ID": row["USER-ID"],
    "SECS-TO-EXPIRE": row["SECS-TO-EXPIRE"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOTARResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "CONNECTION-ID": response["CONNECTION-ID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "KMF-IPV4-ADDR": response["KMF-IPV4-ADDR"],
    "KMF-PORT": response["KMF-PORT"],
    "KMF-RADIO-SET-IDENTIFIER": response["KMF-RADIO-SET-IDENTIFIER"],
    "IWF-RADIO-SET-IDENTIFIER": response["IWF-RADIO-SET-IDENTIFIER"],
    "MESSAGE-NUMBER-PERIOD": response["MESSAGE-NUMBER-PERIOD"],
    "PRIVATE-CALL-SLN": response["PRIVATE-CALL-SLN"],
    "GROUP-REGROUP-SLN": response["GROUP-REGROUP-SLN"],
    "OTAR-REGISTRATION-TIMER": response["OTAR-REGISTRATION-TIMER"],
    "OPT-SEND-REKEY-REQ": response["OPT-SEND-REKEY-REQ"],
    "OPT-REVERSE-WARM-START": response["OPT-REVERSE-WARM-START"],
    "OPT-DE-REGISTRATION": response["OPT-DE-REGISTRATION"],
    "OPT-HELLO-MAC-REPLY": response["OPT-HELLO-MAC-REPLY"],
    "OPT-KEY-ASSIGNMENT": response["OPT-KEY-ASSIGNMENT"],
    "KEEP-ALIVE-TIMER": response["KEEP-ALIVE-TIMER"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOTARUpdateBody = (data: any) => {
  return JSON.stringify({
    "KMF-IPV4-ADDR": data["KMF-IPV4-ADDR"],
    "KMF-PORT": data["KMF-PORT"],
    "KMF-RADIO-SET-IDENTIFIER": data["KMF-RADIO-SET-IDENTIFIER"],
    "IWF-RADIO-SET-IDENTIFIER": data["IWF-RADIO-SET-IDENTIFIER"],
    "MESSAGE-NUMBER-PERIOD": data["MESSAGE-NUMBER-PERIOD"],
    "PRIVATE-CALL-SLN": data["PRIVATE-CALL-SLN"],
    "GROUP-REGROUP-SLN": data["GROUP-REGROUP-SLN"],
    "OTAR-REGISTRATION-TIMER": data["OTAR-REGISTRATION-TIMER"],
    "OPT-SEND-REKEY-REQ": data["OPT-SEND-REKEY-REQ"],
    "OPT-REVERSE-WARM-START": data["OPT-REVERSE-WARM-START"],
    "OPT-DE-REGISTRATION": data["OPT-DE-REGISTRATION"],
    "OPT-HELLO-MAC-REPLY": data["OPT-HELLO-MAC-REPLY"],
    "OPT-KEY-ASSIGNMENT": data["OPT-KEY-ASSIGNMENT"],
    "KEEP-ALIVE-TIMER": data["KEEP-ALIVE-TIMER"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMCPTTUsersUpdateBody = (data: any) => {
  return JSON.stringify({
    ...(data &&
      data["DISPLAY-NAME"] && {
        "DISPLAY-NAME": data["DISPLAY-NAME"],
      }),
    ...(data &&
      data["MAPPED-P25-ID"] && {
        "MAPPED-P25-ID": data["MAPPED-P25-ID"],
      }),
    ...(data &&
      data["MAPPED-DMR-ID"] && {
        "MAPPED-DMR-ID": data["MAPPED-DMR-ID"],
      }),
    ...(data &&
      data["MAPPED-ROIP-ID"] && {
        "MAPPED-ROIP-ID": data["MAPPED-ROIP-ID"],
      }),
    ...(data &&
      data["MAPPED-DFSI-ID"] && {
        "MAPPED-DFSI-ID": data["MAPPED-DFSI-ID"],
      }),
    ...(data &&
      data["MUTUAL-AID"] !== undefined &&
      [true, false].includes(data["MUTUAL-AID"]) && {
        "MUTUAL-AID": data["MUTUAL-AID"],
      }),
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMCPTTUsersResponseRow = (response: any) => {
  let selectedMappedField = "";
  if (response["MAPPED-P25-ID"] && response["MAPPED-DFSI-ID"]) {
    selectedMappedField = "MAPPED-P25-ID + MAPPED-DFSI-ID";
  } else if (response["MAPPED-P25-ID"] && !response["MAPPED-DFSI-ID"]) {
    selectedMappedField = "MAPPED-P25-ID";
  } else if (response["MAPPED-DFSI-ID"] && !response["MAPPED-P25-ID"]) {
    selectedMappedField = "MAPPED-DFSI-ID";
  } else if (response["MAPPED-DMR-ID"]) {
    selectedMappedField = "MAPPED-DMR-ID";
  } else if (response["MAPPED-ROIP-ID"]) {
    selectedMappedField = "MAPPED-ROIP-ID";
  }
  return {
    Ref: response.Ref,
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "ORG-ID": response["ORG-ID"],
    "MCPTT-ID": response["MCPTT-ID"],
    "DISPLAY-NAME": response["DISPLAY-NAME"],
    "MAPPED-P25-ID": response["MAPPED-P25-ID"],
    "MAPPED-DMR-ID": response["MAPPED-DMR-ID"],
    "MAPPED-ROIP-ID": response["MAPPED-ROIP-ID"],
    "MAPPED-DFSI-ID": response["MAPPED-DFSI-ID"],
    "SELECTED-MAPPED-FIELD": selectedMappedField,
    "MUTUAL-AID": response["MUTUAL-AID"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLMRUsersResponseRow = (response: any) => {
  return {
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    Ref: response.Ref,
    "ORG-ID": response["ORG-ID"],
    "LMR-ID": response["LMR-ID"],
    "LMR-NW-TYPE": response["LMR-NW-TYPE"],
    "MAPPED-MCPTT-ID": response["MAPPED-MCPTT-ID"],
    "DISPLAY-NAME": response["DISPLAY-NAME"],
    "MUTUAL-AID": response["MUTUAL-AID"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLMRUsersUpdateBody = (data: any) => {
  return JSON.stringify({
    ...(data &&
      data["LMR-NW-TYPE"] && {
        "LMR-NW-TYPE": data["LMR-NW-TYPE"],
      }),
    ...(data &&
      data["MAPPED-MCPTT-ID"] && {
        "MAPPED-MCPTT-ID": data["MAPPED-MCPTT-ID"],
      }),
    ...(data &&
      data["DISPLAY-NAME"] && {
        "DISPLAY-NAME": data["DISPLAY-NAME"],
      }),
    ...(data &&
      data["MUTUAL-AID"] && {
        "MUTUAL-AID": data["MUTUAL-AID"],
      }),
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformCannedMessageResponseRow = (response: any) => {
  return {
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    Ref: response.Ref,
    "ORG-ID": response["ORG-ID"],
    "MSG-ID": response["MSG-ID"],
    "CANNED-TEXT": response["CANNED-TEXT"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformInstanceRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.ID,
    HOSTNAME: row.HOSTNAME,
    INSTANCE: row.INSTANCE,
    "ENGINE-ID": row["ENGINE-ID"],
    "STARTED-AT": row["STARTED-AT"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformInstanceResponseRow = (response: any) => {
  return {
    ID: response.ID,
    HOSTNAME: response.HOSTNAME,
    INSTANCE: response.INSTANCE,
    "ENGINE-ID": response["ENGINE-ID"],
    "STARTED-AT": response["STARTED-AT"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOrganizationResponseRow = (response: any) => {
  return {
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "ORG-ID": response["ORG-ID"],
    "ORG-NAME": response["ORG-NAME"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOrganizationProfileResponseRow = (response: any) => {
  return {
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "ORG-ID": response["ORG-ID"],
    "UNIT-SLN": response["UNIT-SLN"],
    "UNIT-SECURITY": response["UNIT-SECURITY"],
    "DFSI-UNIT-CONTROL-ATTEMPT-LIMIT":
      response["DFSI-UNIT-CONTROL-ATTEMPT-LIMIT"],
    "DFSI-UNIT-CONTROL-RETRY-TIME": response["DFSI-UNIT-CONTROL-RETRY-TIME"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOrganizationUpdateBody = (data: any) => {
  return JSON.stringify({
    "ORG-NAME": data["ORG-NAME"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformOrgProfileUpdateBody = (data: any) => {
  return JSON.stringify({
    "UNIT-SLN": data["UNIT-SLN"],
    "UNIT-SECURITY": data["UNIT-SECURITY"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformFaultsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row["ROW-ID"],
    "EVENT-ID": row["EVENT-ID"],
    HOSTNAME: row.HOSTNAME,
    INSTANCE: row.INSTANCE,
    VERSION: row.VERSION,
    CreatedAt: row.CreatedAt,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformFaultResponseRow = (response: any) => {
  return {
    "ROW-ID": response["ROW-ID"],
    "EVENT-ID": response["EVENT-ID"],
    HOSTNAME: response.HOSTNAME,
    INSTANCE: response.INSTANCE,
    VERSION: response.VERSION,
    CreatedAt: response.CreatedAt,
    "ADDITIONAL-INFO": JSON.stringify(response["ADDITIONAL-INFO"]),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformInventoryRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row["EVENT-ID"],
    "EVENT-ID": row["EVENT-ID"],
    "EVENT-NAME": row["EVENT-NAME"],
    "EVENT-TYPE": row["EVENT-TYPE"],
    "EVENT-PERCEIVED-SEVERITY": row["EVENT-PERCEIVED-SEVERITY"],
    "EVENT-SYMPTOMS": row["EVENT-SYMPTOMS"],
    "SOURCE-COMPONENT": row["SOURCE-COMPONENT"],
    "INHIBITED-AT": row["INHIBITED-AT"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformInventoryResponseRow = (response: any) => {
  return {
    "EVENT-ID": response["EVENT-ID"],
    "EVENT-NAME": response["EVENT-NAME"],
    "EVENT-PERCEIVED-SEVERITY": response["EVENT-PERCEIVED-SEVERITY"],
    "EVENT-SYMPTOMS": response["EVENT-SYMPTOMS"],
    "SOURCE-COMPONENT": response["SOURCE-COMPONENT"],
    "INHIBITED-AT": response["INHIBITED-AT"],
    "ALARM-TYPE": response["ALARM-TYPE"],
    CreatedAt: response.CreatedAt,
    "EVENT-DESCRIPTION": response["EVENT-DESCRIPTION"],
    "EVENT-PROBABLE-CAUSE": response["EVENT-PROBABLE-CAUSE"],
    "EVENT-RESOLUTION": response["EVENT-RESOLUTION"],
    "EVENT-SPECIFIC-PROBLEM": response["EVENT-SPECIFIC-PROBLEM"],
    "EVENT-TYPE": response["EVENT-TYPE"],
    "SOURCE-APPLICATION": response["SOURCE-APPLICATION"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformAlarmRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row["NOTIFICATION-ID"],
    "NOTIFICATION-ID": row["NOTIFICATION-ID"],
    "EVENT-ID": row["EVENT-ID"],
    HOSTNAME: row.HOSTNAME,
    CreatedAt: row.CreatedAt,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformAlarmsResponseRows = (response: any) => {
  return {
    VERSION: response.VERSION,
    "NOTIFICATION-ID": response["NOTIFICATION-ID"],
    INSTANCE: response.INSTANCE,
    HOSTNAME: response.HOSTNAME,
    "EVENT-ID": response["EVENT-ID"],
    CreatedAt: response.CreatedAt,
    COMMENTS: response.COMMENTS,
    "ADDITIONAL-INFO": response["ADDITIONAL-INFO"],
    "APP-REF": response["APP-REF"],
    "SUB-COMPONENT": response["SUB-COMPONENT"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformHistoricalAlarmsRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row["ROW-ID"],
    "EVENT-ID": row["EVENT-ID"],
    "ROW-TYPE": row["ROW-TYPE"],
    HOSTNAME: row.HOSTNAME,
    CreatedAt: row.CreatedAt,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformHistoricalAlarmsResponseRows = (response: any) => {
  return {
    VERSION: response.VERSION,
    "ROW-ID": response["ROW-ID"],
    "ROW-TYPE": response["ROW-TYPE"],
    "NOTIFICATION-ID": response["NOTIFICATION-ID"],
    INSTANCE: response.INSTANCE,
    HOSTNAME: response.HOSTNAME,
    "EVENT-ID": response["EVENT-ID"],
    CreatedAt: response.CreatedAt,
    COMMENTS: response.COMMENTS,
    "ADDITIONAL-INFO": response["ADDITIONAL-INFO"],
    "APP-REF": response["APP-REF"],
    "SUB-COMPONENT": response["SUB-COMPONENT"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLocationUpdateBody = (data: any) => {
  return JSON.stringify({
    "IWF-DOMAIN-NAME": data["IWF-DOMAIN-NAME"],
  });
};

export function fetchFixedStationVoiceIPV4Address(data: any) {
  if (data["DFSI-VERSION"] && data["IWF-ROLE"]) {
    if (+data["DFSI-VERSION"] === 2 && data["IWF-ROLE"] === "Station") {
      return data["FIXED-STATION-VOICE-IPV4-ADDR"];
    }
    if (+data["DFSI-VERSION"] === 1)
      return data["FIXED-STATION-VOICE-IPV4-ADDR"];
  }
  return undefined;
}

export function fetchHostControlIPV4Address(data: any) {
  if (data["DFSI-VERSION"] && data["IWF-ROLE"]) {
    if (data["IWF-ROLE"] === "Host") {
      return data["HOST-CONTROL-IPV4-ADDR"];
    }
  }
  return undefined;
}

export function fetchHostVoiceIPV4Address(data: any) {
  if (data["DFSI-VERSION"] && data["IWF-ROLE"]) {
    if (data["IWF-ROLE"] === "Host") {
      return data["HOST-VOICE-IPV4-ADDR"];
    }
  }
  return undefined;
}

export function fetchHostControlPort(data: any) {
  if (data["DFSI-VERSION"] && data["IWF-ROLE"]) {
    if (data["IWF-ROLE"] === "Host") {
      return data["HOST-CONTROL-PORT"];
    }
  }
  return undefined;
}

export const fetchConnectivityTxTimerValue = (data: any) => {
  if (
    data["DFSI-VERSION"] &&
    data["IWF-ROLE"] &&
    +data["DFSI-VERSION"] === 2 &&
    data["IWF-ROLE"] === "Host"
  )
    return data["CONNECTIVITY-TX-TIMER"];

  return undefined;
};

export const fetchFSHeartbeatPeriodValue = (data: any) => {
  return data["DFSI-VERSION"] &&
    data["IWF-ROLE"] &&
    +data["DFSI-VERSION"] === 1 &&
    data["IWF-ROLE"] === "Host"
    ? data["FS-HEARTBEAT-PERIOD"]
    : undefined;
};

export const fetchHostHeartbeatPeriodValue = (data: any) => {
  if (
    data["DFSI-VERSION"] &&
    data["IWF-ROLE"] &&
    +data["DFSI-VERSION"] === 1 &&
    data["IWF-ROLE"] === "Host"
  )
    return data["HOST-HEARTBEAT-PERIOD"];

  return undefined;
};

export const fetchFixedStationVoterReportingPeriodValue = (data: any) => {
  return data["DFSI-VERSION"] &&
    data["IWF-ROLE"] &&
    data["IWF-ROLE"] === "Station"
    ? data["FIXED-STATION-VOTER-REPORTING-PERIOD"]
    : undefined;
};

export const fetchFixedStationTransmitterReportIntervalValue = (data: any) => {
  return data["DFSI-VERSION"] &&
    data["IWF-ROLE"] &&
    +data["DFSI-VERSION"] === 2 &&
    data["IWF-ROLE"] === "Station"
    ? data["FIXED-STATION-TRANSMITTER-REPORT-INTERVAL"]
    : undefined;
};

export const fetchDataIPV4AddressValue = (data: any) => {
  return data["DFSI-VERSION"] && data["IWF-ROLE"] && +data["DFSI-VERSION"] === 2
    ? data["DATA-IPV4-ADDR"]
    : undefined;
};

export const fetchDataPortValue = (data: any) => {
  return data["DFSI-VERSION"] && data["IWF-ROLE"] && +data["DFSI-VERSION"] === 2
    ? data["DATA-PORT"]
    : undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIConnectionUpdateBody = (data: any) => {
  return JSON.stringify({
    ...(data["HOST-CONTROL-IPV4-ADDR"] && {
      "HOST-CONTROL-IPV4-ADDR": fetchHostControlIPV4Address(data),
    }),
    ...(data["HOST-CONTROL-PORT"] && {
      "HOST-CONTROL-PORT": fetchHostControlPort(data),
    }),
    ...(data["HOST-VOICE-IPV4-ADDR"] && {
      "HOST-VOICE-IPV4-ADDR": fetchHostVoiceIPV4Address(data),
    }),
    "VOICE-PORT": data["VOICE-PORT"],
    "FIXED-STATION-CONTROL-IPV4-ADDR": data["FIXED-STATION-CONTROL-IPV4-ADDR"],
    "FIXED-STATION-CONTROL-PORT": data["FIXED-STATION-CONTROL-PORT"],
    ...(data["CONNECTIVITY-TX-TIMER"] && {
      "CONNECTIVITY-TX-TIMER": fetchConnectivityTxTimerValue(data),
    }),
    "CONNECTIVITY-LOSS-LIMIT": data["CONNECTIVITY-LOSS-LIMIT"],
    "CONTROL-RETRY-TIME": data["CONTROL-RETRY-TIME"],
    "CONTROL-ATTEMPT-LIMIT": data["CONTROL-ATTEMPT-LIMIT"],
    "ACKNOWLEDGEMENT-DELAY": data["ACKNOWLEDGEMENT-DELAY"],
    ...(data["FIXED-STATION-VOICE-IPV4-ADDR"] && {
      "FIXED-STATION-VOICE-IPV4-ADDR": fetchFixedStationVoiceIPV4Address(data),
    }),
    ...(data["FS-HEARTBEAT-PERIOD"] && {
      "FS-HEARTBEAT-PERIOD": fetchFSHeartbeatPeriodValue(data),
    }),
    ...(data["HOST-HEARTBEAT-PERIOD"] && {
      "HOST-HEARTBEAT-PERIOD": fetchHostHeartbeatPeriodValue(data),
    }),
    ...(data["DATA-PORT"] && {
      "DATA-PORT": fetchDataPortValue(data),
    }),
    ...(data["DATA-IPV4-ADDR"] && {
      "DATA-IPV4-ADDR": fetchDataIPV4AddressValue(data),
    }),
    ...(data["DFSI-VERSION"] &&
      +data["DFSI-VERSION"] === 2 && {
        "OUTBOUND-MAX-CAI-DATA-BLOCKS": data["OUTBOUND-MAX-CAI-DATA-BLOCKS"],
      }),
    ...(data["DFSI-VERSION"] &&
      +data["DFSI-VERSION"] === 2 && {
        "INBOUND-MAX-CAI-DATA-BLOCKS": data["INBOUND-MAX-CAI-DATA-BLOCKS"],
      }),
    "INBOUND-REPEAT": data["INBOUND-REPEAT"],
    ...(data["FIXED-STATION-VOTER-REPORTING-PERIOD"] && {
      "FIXED-STATION-VOTER-REPORTING-PERIOD":
        fetchFixedStationVoterReportingPeriodValue(data),
    }),
    ...(data["FIXED-STATION-TRANSMITTER-REPORT-INTERVAL"] && {
      "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL":
        fetchFixedStationTransmitterReportIntervalValue(data),
    }),
  });
};

export const handleCTCnCDCvalues = (
  field?: string,
  ctcvalue?: string,
  cdcValue?: string,
  isChecked?: boolean
) => {
  if (ctcvalue === "None" && cdcValue === "None" && !isChecked) return 1;
  if (isChecked) return 1;
  if (field === "ctc" && cdcValue === "None" && ctcvalue !== "None") return 1;
  if (field === "cdc" && ctcvalue === "None" && cdcValue !== "None") return 1;
  return null;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIChannelUpdateBody = (
  dfsiVersion: number,
  data: any,
  isChecked: boolean
) => {
  return JSON.stringify({
    ...(data["CHANNEL-MODE"] &&
      data["CHANNEL-MODE"] === "Digital" && {
        "OUTBOUND-NETWORK-ACCESS-CODE":
          data["OUTBOUND-NETWORK-ACCESS-CODE"] &&
          +data["OUTBOUND-NETWORK-ACCESS-CODE"],
      }),
    ...(data["CHANNEL-MODE"] &&
      data["CHANNEL-MODE"] === "Digital" && {
        "INBOUND-NETWORK-ACCESS-CODE":
          data["INBOUND-NETWORK-ACCESS-CODE"] &&
          +data["INBOUND-NETWORK-ACCESS-CODE"],
      }),
    ...(data["CHANNEL-MODE"] &&
      data["CHANNEL-MODE"] === "Digital" && {
        "OTA-GROUP-ID": data["OTA-GROUP-ID"] && +data["OTA-GROUP-ID"],
      }),
    "CHANNEL-MODE": data["CHANNEL-MODE"],
    ...(data["CHANNEL-MODE"] &&
      data["CHANNEL-MODE"] === "Analog" &&
      dfsiVersion === 2 &&
      handleCTCnCDCvalues(
        "ctc",
        data["CTC-SQUELCH-SYSTEM"],
        data["CDC-SQUELCH-SYSTEM"],
        isChecked
      ) && {
        "CTC-SQUELCH-SYSTEM":
          isChecked ||
          (data["CTC-SQUELCH-SYSTEM"] === "None" &&
            data["CDC-SQUELCH-SYSTEM"] === "None")
            ? 0
            : data["CTC-SQUELCH-SYSTEM"] && +data["CTC-SQUELCH-SYSTEM"],
      }),
    ...(data["CHANNEL-MODE"] &&
      data["CHANNEL-MODE"] === "Analog" &&
      dfsiVersion === 2 &&
      handleCTCnCDCvalues(
        "cdc",
        data["CTC-SQUELCH-SYSTEM"],
        data["CDC-SQUELCH-SYSTEM"],
        isChecked
      ) && {
        "CDC-SQUELCH-SYSTEM":
          isChecked ||
          (data["CTC-SQUELCH-SYSTEM"] === "None" &&
            data["CDC-SQUELCH-SYSTEM"] === "None")
            ? 0
            : data["CDC-SQUELCH-SYSTEM"] && +data["CDC-SQUELCH-SYSTEM"],
      }),
  });
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIGroupUpdateBody = (data: any) => {
  return JSON.stringify({
    "CHANNEL-IDS": data["CHANNEL-IDS"].map((item: any) => ({
      "CHANNEL-ID": item.id,
    })),
    "ENCRYPTION-ENABLED": data["ENCRYPTION-ENABLED"],
    ...(data &&
      data["ENCRYPTION-ENABLED"] && {
        SLN: data.SLN,
      }),
  });
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLocationResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "IWF-ID": response["IWF-ID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "IWF-LOCATION-NAME": response["IWF-LOCATION-NAME"],
    "IWF-DOMAIN-NAME": response["IWF-DOMAIN-NAME"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIConnectionResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "CONNECTION-ID": response["CONNECTION-ID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "DFSI-VERSION": response["DFSI-VERSION"],
    "IWF-ROLE": response["IWF-ROLE"],
    "HOST-CONTROL-IPV4-ADDR": response["HOST-CONTROL-IPV4-ADDR"],
    "HOST-CONTROL-PORT": response["HOST-CONTROL-PORT"],
    "HOST-VOICE-IPV4-ADDR": response["HOST-VOICE-IPV4-ADDR"],
    "VOICE-PORT": response["VOICE-PORT"],
    "DATA-IPV4-ADDR": response["DATA-IPV4-ADDR"],
    "DATA-PORT": response["DATA-PORT"],
    "FIXED-STATION-CONTROL-IPV4-ADDR":
      response["FIXED-STATION-CONTROL-IPV4-ADDR"],
    "FIXED-STATION-VOICE-IPV4-ADDR": response["FIXED-STATION-VOICE-IPV4-ADDR"],
    "FIXED-STATION-CONTROL-PORT": response["FIXED-STATION-CONTROL-PORT"],
    "CONNECTIVITY-TX-TIMER": response["CONNECTIVITY-TX-TIMER"],
    "CONNECTIVITY-LOSS-LIMIT": response["CONNECTIVITY-LOSS-LIMIT"],
    "FS-HEARTBEAT-PERIOD": response["FS-HEARTBEAT-PERIOD"],
    "HOST-HEARTBEAT-PERIOD": response["HOST-HEARTBEAT-PERIOD"],
    "CONTROL-RETRY-TIME": response["CONTROL-RETRY-TIME"],
    "CONTROL-ATTEMPT-LIMIT": response["CONTROL-ATTEMPT-LIMIT"],
    "OUTBOUND-MAX-CAI-DATA-BLOCKS": response["OUTBOUND-MAX-CAI-DATA-BLOCKS"],
    "INBOUND-MAX-CAI-DATA-BLOCKS": response["INBOUND-MAX-CAI-DATA-BLOCKS"],
    "ACKNOWLEDGEMENT-DELAY": response["ACKNOWLEDGEMENT-DELAY"],
    "INBOUND-REPEAT": response["INBOUND-REPEAT"],
    "FIXED-STATION-VOTER-REPORTING-PERIOD":
      response["FIXED-STATION-VOTER-REPORTING-PERIOD"],
    "FIXED-STATION-TRANSMITTER-REPORT-INTERVAL":
      response["FIXED-STATION-TRANSMITTER-REPORT-INTERVAL"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIChannelResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "CONNECTION-ID": response["CONNECTION-ID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "CHANNEL-ID": response["CHANNEL-ID"],
    "OUTBOUND-NETWORK-ACCESS-CODE": response["OUTBOUND-NETWORK-ACCESS-CODE"],
    "INBOUND-NETWORK-ACCESS-CODE": response["INBOUND-NETWORK-ACCESS-CODE"],
    "OTA-GROUP-ID": response["OTA-GROUP-ID"],
    "CHANNEL-MODE": response["CHANNEL-MODE"],
    "CTC-SQUELCH-SYSTEM":
      ((response["CHANNEL-MODE"] === "Analog" &&
        response["CTC-SQUELCH-SYSTEM"]) ||
      (response["CHANNEL-MODE"] === "Analog" &&
        response["CTC-SQUELCH-SYSTEM"] === 0)
        ? response["CTC-SQUELCH-SYSTEM"].toString()
        : "None") ||
      (response["CHANNEL-MODE"] === "Digital" && response["CTC-SQUELCH-SYSTEM"]
        ? response["CTC-SQUELCH-SYSTEM"]
        : null),
    "CDC-SQUELCH-SYSTEM":
      (response["CHANNEL-MODE"] === "Analog" && response["CDC-SQUELCH-SYSTEM"]
        ? response["CDC-SQUELCH-SYSTEM"].toString()
        : "None") ||
      (response["CHANNEL-MODE"] === "Digital" && response["CDC-SQUELCH-SYSTEM"]
        ? response["CDC-SQUELCH-SYSTEM"]
        : null),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformDFSIGroupResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "DFSI-LOGICAL-GID": response["DFSI-LOGICAL-GID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "DFSI-GROUP-NAME": response["DFSI-GROUP-NAME"],
    "IWF-ID": response["IWF-ID"],
    "ENCRYPTION-ENABLED": response["ENCRYPTION-ENABLED"],
    SLN: response.SLN,
    "CHANNEL-IDS": response["CHANNEL-IDS"].map((row: any) => ({
      id: row["CHANNEL-ID"],
      text: row["CHANNEL-ID"],
    })),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMappingRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row["ORG-ID"],
    "IWF-ID": row["IWF-ID"],
    "ORG-ID": row["ORG-ID"],
    CreationDate: row.CreationDate,
    ModificationDate: row.ModificationDate,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMappingResponseRow = (response: any) => {
  return {
    "IWF-ID": response["IWF-ID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "ORG-ID": response["ORG-ID"],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMappingUpdateBody = (data: any) => {
  return JSON.stringify({
    "IWF-ID": data["IWF-ID"],
    "ORG-ID": data["ORG-ID"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformTranslationUpdateBody = (data: any) => {
  return JSON.stringify({
    FROM: data.FROM,
    EFI: data.EFI,
    EFO: data.EFO,
    TO: data.TO,
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLMRGroupsUpdateBody = (data: any) => {
  return JSON.stringify({
    ...(data &&
      data["LMR-NW-TYPE"] && {
        "LMR-NW-TYPE": data["LMR-NW-TYPE"],
      }),
    ...(data &&
      data["MAPPED-MCPTT-GID"] && {
        "MAPPED-MCPTT-GID": data["MAPPED-MCPTT-GID"],
      }),
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformLMRGroupProfileUpdateBody = (data: any) => {
  return JSON.stringify({
    "GROUP-ACCESS-PERMISSION": data["GROUP-ACCESS-PERMISSION"],
    "GROUP-EMERGENCY-CAPABLE": data["GROUP-EMERGENCY-CAPABLE"],
    "GROUP-CC-SETUP-TIME": data["GROUP-CC-SETUP-TIME"],
    "GROUP-SECURITY": data["GROUP-SECURITY"],
    "GROUP-RFSS-ID": data["GROUP-RFSS-ID"],
    "GROUP-SLN": data["GROUP-SLN"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMCPTTGroupProfileUpdateBody = (data: any) => {
  return JSON.stringify({
    "GROUP-ACCESS-PERMISSION": data["GROUP-ACCESS-PERMISSION"],
    "GROUP-EMERGENCY-CAPABLE": data["GROUP-EMERGENCY-CAPABLE"],
    "GROUP-CC-SETUP-TIME": data["GROUP-CC-SETUP-TIME"],
    "GROUP-SECURITY": data["GROUP-SECURITY"],
    "GROUP-PRIORITY": data["GROUP-PRIORITY"],
    "GROUP-SLN": data["GROUP-SLN"],
    "GROUP-EMERGENCY-PREEMPTION": data["GROUP-EMERGENCY-PREEMPTION"],
    "GROUP-RF-HANG-TIME": data["GROUP-RF-HANG-TIME"],
    "GROUP-INTERRUPT-MODE": data["GROUP-INTERRUPT-MODE"],
    "GROUP-EXPIRATION-PERIOD": data["GROUP-EXPIRATION-PERIOD"],
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMCPTTGroupsUpdateBody = (data: any) => {
  return JSON.stringify({
    ...(data &&
      data["MAPPED-P25-TG-ID"] && {
        "MAPPED-P25-TG-ID": data["MAPPED-P25-TG-ID"],
      }),
    ...(data &&
      data["MAPPED-DMR-TG-ID"] && {
        "MAPPED-DMR-TG-ID": data["MAPPED-DMR-TG-ID"],
      }),
    ...(data &&
      data["MAPPED-ROIP-TG-ID"] && {
        "MAPPED-ROIP-TG-ID": data["MAPPED-ROIP-TG-ID"],
      }),
    ...(data &&
      data["MAPPED-DFSI-TG-ID"] && {
        "MAPPED-DFSI-TG-ID": data["MAPPED-DFSI-TG-ID"],
      }),
  });
};

export const createSearchConfig = (
  searchFields: string[],
  mainArray: Array<any>
) => {
  const isPropertyPresent = mainArray.some(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (obj: any) => "fields" in obj
  );
  if (!isPropertyPresent) {
    return mainArray
      .filter((obj: any) => searchFields.includes(obj.name))
      .map((obj: any) => {
        return {
          name: obj.name,
          label: obj.label,
          tooltipText: obj.tooltipText,
          type: obj.type,
        };
      });
  }
  const result = mainArray
    .map((row) => row.fields)
    .map((fieldsArray) =>
      fieldsArray.filter((obj: any) => searchFields.includes(obj.name))
    )
    .filter((configArr) => configArr.length > 0)
    .flatMap((ele) => ele, 1)
    .map((obj: any) => {
      if (obj.type === "MappedValueDropdown") {
        const newArray = obj.items.map((item: any, i: string | number) => {
          return {
            name: item,
            label: item,
            tooltipText: obj.placeholder[i] || obj.placeholder[i],
            type: "TEXT",
          };
        });
        return newArray;
      }
      return {
        name: obj.name,
        label: obj.label,
        tooltipText: obj.tooltipText || obj.fieldTooltipText,
        type: obj.type,
      };
    });
  return result.flatMap((ele) => ele, 1);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformP25UnitData = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    MONITOR: row.MONITOR !== undefined ? String(row.MONITOR) : row.MONITOR,
    "TARGET-ID": row["TARGET-ID"],
    PERIOD: row.PERIOD,
    RFSS: row.RFSS,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformP25UnitResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "TARGET-ID": response["TARGET-ID"],
    PERIOD: response.PERIOD,
    RFSS: response.RFSS,
    MONITOR:
      response.MONITOR !== undefined
        ? String(response.MONITOR)
        : response.MONITOR,
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformP25GroupData = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    MONITOR: row.MONITOR !== undefined ? String(row.MONITOR) : row.MONITOR,
    "TARGET-ID": row["TARGET-ID"],
    PERIOD: row.PERIOD,
    RFSS: row.RFSS,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformP25GroupResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "TARGET-ID": response["TARGET-ID"],
    PERIOD: response.PERIOD,
    RFSS: response.RFSS,
    MONITOR:
      response.MONITOR !== undefined
        ? String(response.MONITOR)
        : response.MONITOR,
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformFixedNetworkElementData = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    MONITOR: row.MONITOR !== undefined ? String(row.MONITOR) : row.MONITOR,
    "TARGET-ID": row["TARGET-ID"],
    PERIOD: row.PERIOD,
    RFSS: row.RFSS,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformFixedNetworkElementResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "TARGET-ID": response["TARGET-ID"],
    PERIOD: response.PERIOD,
    RFSS: response.RFSS,
    MONITOR:
      response.MONITOR !== undefined
        ? String(response.MONITOR)
        : response.MONITOR,
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformP25AffiliationsResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "TARGET-ID": response["TARGET-ID"],
    PERIOD: response.PERIOD,
    RFSS: response.RFSS,
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformP25GAffiliationsData = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "TARGET-ID": row["TARGET-ID"],
    PERIOD: row.PERIOD,
    RFSS: row.RFSS,
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformIKIResponseRow = (response: any) => {
  return {
    Ref: response.Ref,
    "CONNECTION-ID": response["CONNECTION-ID"],
    CreationDate: response.CreationDate,
    ModificationDate: response.ModificationDate,
    "IKI-IPV4-ADDR": response["IKI-IPV4-ADDR"],
    "IKI-PORT": response["IKI-PORT"],
    "IKI-RADIO-SET-IDENTIFIER": response["IKI-RADIO-SET-IDENTIFIER"],
    "IWF-RADIO-SET-IDENTIFIER": response["IWF-RADIO-SET-IDENTIFIER"],
    "IKI-CERTIFICATE": response["IKI-CERTIFICATE"],
    "IKI-NAME": response["IKI-NAME"],
    TYPE: undefined,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformIKIRows = (rows: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => ({
    id: row.Ref,
    "CONNECTION-ID": row["CONNECTION-ID"],
    "IKI-NAME": row["IKI-NAME"],
    "IKI-RADIO-SET-IDENTIFIER": row["IKI-RADIO-SET-IDENTIFIER"],
    "IWF-RADIO-SET-IDENTIFIER": row["IWF-RADIO-SET-IDENTIFIER"],
    "IKI-PORT": row["IKI-PORT"],
    "IKI-IPV4-ADDR": row["IKI-IPV4-ADDR"],
    "IKI-CERTIFICATE": row["IKI-CERTIFICATE"],
  }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformIKIUpdateBody = (data: any) => {
  return JSON.stringify({
    "IKI-IPV4-ADDR": data["IKI-IPV4-ADDR"],
    "IKI-PORT": data["IKI-PORT"],
    "IKI-RADIO-SET-IDENTIFIER": data["IKI-RADIO-SET-IDENTIFIER"],
    "IWF-RADIO-SET-IDENTIFIER": data["IWF-RADIO-SET-IDENTIFIER"],
    "IKI-NAME": data["IKI-NAME"],
    "IKI-CERTIFICATE": data["IKI-CERTIFICATE"],
  });
};

// Function to download the Certificate for IKI Connections
export const downloadCertificate = (data: any, filename: string) => {
  // Ensure the filename ends with .pem or .crt if not specified
  const fileNameWithExt =
    filename.endsWith(".pem") || filename.endsWith(".crt")
      ? filename
      : `${filename}.pem`;

  // Create a Blob with the PEM content
  const blob = new Blob([data], { type: "application/x-pem-file" });

  // Create a URL for the Blob
  const url = window.URL.createObjectURL(blob);

  // Create an anchor tag for downloading
  const a = document.createElement("a");

  // Set the URL and download attribute of the anchor tag
  a.href = url;
  a.download = fileNameWithExt;
  // Trigger the download by clicking the anchor tag
  a.click();
};

// To get combination of port and IP address field to verify unqiuness
export const getUniqueCombination = (version: number, role: string) => {
  let fieldCombo: string[][] = [];
  if (version === 1 && role === "Station") {
    fieldCombo = [
      ["VOICE-PORT", "FIXED-STATION-CONTROL-IPV4-ADDR"],
      ["FIXED-STATION-CONTROL-PORT", "FIXED-STATION-CONTROL-IPV4-ADDR"],
    ];
  }
  if (version === 2 && role === "Station") {
    fieldCombo = [
      ["DATA-PORT", "DATA-IPV4-ADDR"],
      ["VOICE-PORT", "FIXED-STATION-VOICE-IPV4-ADDR"],
      ["FIXED-STATION-CONTROL-PORT", "FIXED-STATION-CONTROL-IPV4-ADDR"],
    ];
  }
  if (version === 1 && role === "Host") {
    fieldCombo = [
      ["HOST-CONTROL-PORT", "HOST-CONTROL-IPV4-ADDR"],
      ["VOICE-PORT", "HOST-VOICE-IPV4-ADDR"],
      ["FIXED-STATION-CONTROL-PORT", "FIXED-STATION-CONTROL-IPV4-ADDR"],
    ];
  }
  if (version === 2 && role === "Host") {
    fieldCombo = [
      ["HOST-CONTROL-PORT", "HOST-CONTROL-IPV4-ADDR"],
      ["DATA-PORT", "DATA-IPV4-ADDR"],
      ["VOICE-PORT", "HOST-VOICE-IPV4-ADDR"],
      ["FIXED-STATION-CONTROL-PORT", "FIXED-STATION-CONTROL-IPV4-ADDR"],
    ];
  }
  return fieldCombo;
};
